import { chSupabase } from '../lib/supabase';
import { config } from '../../lib/config';

interface AiResponse<T = unknown> {
  success: boolean;
  data?: T;
  tone_profile_set?: boolean;
  error?: string;
}

interface CaptionResult {
  variations: string[];
}

interface AdaptResult {
  adaptations: Record<string, string>;
}

interface FeedbackRevisionResult {
  caption: string;
  change_note: string;
}

interface DailySummaryResult {
  summary: string;
}

async function callAiAssistant<T>(body: {
  feature: string;
  project_id: string;
  post_id?: string | null;
  payload: Record<string, unknown>;
}): Promise<AiResponse<T>> {
  const { data: { session } } = await chSupabase.auth.getSession();
  if (!session) throw new Error('Não autenticado');

  const url = `${config.supabaseUrl}/functions/v1/ai-assistant`;

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
      'apikey': config.supabaseAnonKey,
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Erro de rede' }));
    throw new Error(err.error || `Erro ${res.status}`);
  }

  return await res.json();
}

export const chAiService = {
  async generateCaption(params: {
    project_id: string;
    post_id?: string | null;
    title: string;
    platform: string;
    content_type: string;
    scheduled_date?: string | null;
    current_caption?: string | null;
  }): Promise<AiResponse<CaptionResult>> {
    return callAiAssistant<CaptionResult>({
      feature: 'generate_caption',
      project_id: params.project_id,
      post_id: params.post_id,
      payload: {
        title: params.title,
        platform: params.platform,
        content_type: params.content_type,
        scheduled_date: params.scheduled_date || null,
        current_caption: params.current_caption || null,
      },
    });
  },

  async adaptPlatforms(params: {
    project_id: string;
    post_id?: string | null;
    caption: string;
    current_platform: string;
    title: string;
  }): Promise<AiResponse<AdaptResult>> {
    return callAiAssistant<AdaptResult>({
      feature: 'adapt_platforms',
      project_id: params.project_id,
      post_id: params.post_id,
      payload: {
        caption: params.caption,
        current_platform: params.current_platform,
        title: params.title,
      },
    });
  },

  async feedbackRevision(params: {
    project_id: string;
    post_id?: string | null;
    current_caption: string;
    client_comments: string[];
    title: string;
  }): Promise<AiResponse<FeedbackRevisionResult>> {
    return callAiAssistant<FeedbackRevisionResult>({
      feature: 'feedback_revision',
      project_id: params.project_id,
      post_id: params.post_id,
      payload: {
        current_caption: params.current_caption,
        client_comments: params.client_comments,
        title: params.title,
      },
    });
  },

  async dailySummary(params: {
    project_id: string;
    digest_data: unknown;
  }): Promise<AiResponse<DailySummaryResult>> {
    return callAiAssistant<DailySummaryResult>({
      feature: 'daily_summary',
      project_id: params.project_id,
      payload: {
        digest_data: params.digest_data,
      },
    });
  },
};
